import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as JspUtil from '../common/JspUtil';
import * as globalActions from '../layout/actionCreators';
import {
    USER_INPUT_USER_NAME_CHANGE,
    USER_INPUT_APP_KEY_CHANGE
} from '../loginpages/actionCreators';
import * as registrationActions from '../registration/actionCreators';
import useAuth from '../auth/useAuth';

/**
 * App context utilities: getLoginApp, getApp, getUiMsg, dispatchers, etc.
 * Replaces TopCompBase lines 305-540.
 *
 * @param {object} jspVariables - from useJspVariables
 * @param {string} alertElementName - from useAlerts
 */
const useAppContext = (jspVariables, alertElementName = '') => {
    const dispatch = useDispatch();
    const appKey = useSelector(state => state.userInputsReducer?.appKey);
    const appUiMessages = useSelector(state => state.globalReducer?.appUiMessages);
    const personalInfo = useSelector(state => state.registrationReducer?.PERSONAL_INFO);
    const { authenticatedApp } = useAuth();

    const getLoginApp = useCallback(() => {
        if (jspVariables?.jspLoginApp) {
            return jspVariables.jspLoginApp;
        }
        return appKey || authenticatedApp || '';
    }, [jspVariables, appKey, authenticatedApp]);

    const getApp = useCallback(() => {
        if (jspVariables?.jspApp) {
            return jspVariables.jspApp;
        }
        if (jspVariables?.jspLoginApp) {
            return jspVariables.jspLoginApp;
        }
        return appKey || authenticatedApp || '';
    }, [jspVariables, appKey, authenticatedApp]);

    const getUiMsg = useCallback((key) => {
        if (appUiMessages) {
            let msg = null;
            appUiMessages.forEach(m => {
                if (m.uiPlaceHolder === key) {
                    msg = m.message;
                }
            });
            if (msg !== null) {
                return msg;
            }
        }
        return '';
    }, [appUiMessages]);

    const getLabelFromJspVariables = useCallback((...args) => {
        return JspUtil.getLabelFromJspVariables(...args);
    }, []);

    const showPasswordLessOtpField = useCallback(() => {
        return 'Y' === jspVariables?.jspPasswordLessEnabled;
    }, [jspVariables]);

    const isPasswordLessPreferred = useCallback(() => {
        return 'Y' === jspVariables?.jspPasswordLessPreferred;
    }, [jspVariables]);

    const isMacOS = useCallback(() => {
        const platform = window.navigator.platform;
        const macOS = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
        return macOS.indexOf(platform) !== -1;
    }, []);

    // Input dispatchers
    const onUserNameChange = useCallback((val) => {
        dispatch({ type: USER_INPUT_USER_NAME_CHANGE, data: val });
    }, [dispatch]);

    const onAppNameChange = useCallback((val) => {
        dispatch({ type: USER_INPUT_APP_KEY_CHANGE, data: val });
        if (val) {
            dispatch(globalActions.getAppUiMessages(val));
        }
    }, [dispatch]);

    const onValueChange = useCallback((event, maxLength) => {
        const { target: { name, value } } = event;
        if (value && maxLength && value.length > maxLength) {
            console.warn('Max Length for this field is ' + maxLength);
            return false;
        }
        let newVal = {};
        newVal[name] = value;
        let merged = { ...personalInfo, ...newVal };
        dispatch({ type: registrationActions.USER_INPUT_PERSONAL_INFO_CHANGE, data: merged });
    }, [dispatch, personalInfo, alertElementName]);

    return {
        appKey,
        getLoginApp,
        getApp,
        getUiMsg,
        getLabelFromJspVariables,
        showPasswordLessOtpField,
        isPasswordLessPreferred,
        isMacOS,
        onUserNameChange,
        onAppNameChange,
        onValueChange,
    };
};

export default useAppContext;
